import { useState } from "react";
import { UsersIcon } from "lucide-react";

import { StakeholdersIntakeTabBar } from "./StakeholdersIntakeTabBar";
import { Column, EmptyState, ErrorText, LoadingIndicator } from "@/components";
import { StakeholderForm } from "@/components/companies/shared/StakeholderForm";
import { StakeholderReadOnly } from "@/components/companies/shared/StakeholderReadOnly";

import { useCreateAddStakeholder } from "@/hooks/createAdds/useCreateAddStakeholder";
import { useGetStakeholders } from "@/hooks/queries/useGetStakeholders";

export const Stakeholders = ({
  isEditing,
  setIsEditing,
}: {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
}) => {
  const { data: stakeholders, isPending, isError } = useGetStakeholders();
  const { mutate: createStakeholder, isPending: isCreateStakeholderPending } =
    useCreateAddStakeholder();

  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);

  const render = () => {
    if (isPending) {
      return <LoadingIndicator variant="medium" />;
    }

    if (isError) {
      return (
        <Column className="h-full items-center justify-center">
          <ErrorText errorMessage="An error occurred while loading stakeholders. Please let the engineers know!" />
        </Column>
      );
    }

    if (!stakeholders.length) {
      return (
        <EmptyState
          title="No stakeholders yet"
          description="Add a stakeholder to get started."
          Icon={UsersIcon}
          primaryButtonName="Add stakeholder"
          handlePrimaryClick={() => {
            setIsEditing(true);
            createStakeholder(undefined, {
              onSuccess: (newStakeholder) => {
                setSelectedId(newStakeholder.id);
              },
            });
          }}
          IsLoading={isCreateStakeholderPending}
        />
      );
    }

    const resolvedSelectedId = selectedId ?? stakeholders[0]?.id;
    const selectedStakeholder = stakeholders.find(
      (s) => s.id === resolvedSelectedId,
    );

    return (
      <Column className="gap-4">
        {/* top offset = combined height of IntakeHeader */}
        <Column className="sticky top-[8.78rem] z-10 bg-white">
          <StakeholdersIntakeTabBar
            stakeholders={stakeholders}
            selectedId={selectedId}
            setIsEditing={setIsEditing}
            setSelectedId={setSelectedId}
          />
        </Column>
        {selectedStakeholder &&
          (isEditing ? (
            <StakeholderForm
              key={selectedStakeholder.id}
              stakeholder={selectedStakeholder}
            />
          ) : (
            <StakeholderReadOnly stakeholder={selectedStakeholder} />
          ))}
      </Column>
    );
  };

  return render();
};
